import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useI18n } from '../../locale/I18nContext';
import { ZoomState } from '../../store/zoom/zoomState';
import { VisualizationState } from '../../store/birdElement/visualizationState';
import { selectSelectionDisabledState, selectZoomState, selectBusyState, selectVisualizationState, selectDragModeState, selectOverviewState, selectMaximizeState, selectElementDecoratorState, selectObjectSearchState, selectLayerVisibilityState, selectWarningState, } from '../../store/selectors';
import { useGetCustomMenuItems } from '../ReportContainer/MenuItemProvider';
import { ObjectToolbar } from '../ObjectToolbar';
import { DragModeState } from '../../store/dragMode/dragModeState';
import { ZoomToolbar } from '../ZoomToolbar';
import { DragMode } from '@sas-dvr/ltjs-gtl';
import { OverviewState } from '../../store/overview/overviewState';
import { BirdState } from '../../store/bird/birdState';
import { Blank, Checkmark, ZoomInButton, ZoomOutButton, ZoomToFit, Pan, RectangularSelection, CircularSelection, FreeformSelection, Zoom, ViewNavigationWindow, NormalView, MaximizeView, Layers, } from '@sas/react-svg';
import { useElementType } from '../ReportContainer/ReportContainerProviders';
import { ElementDecoratorState, } from '../../store/elementDecorator/elementDecoratorState';
import { ObjectSearch } from '../ObjectSearch';
import { LayerVisibilityState, } from '../../store/layerVisibility/layerVisibility';
import { WarningIcon } from './WarningIcon';
function getDragModeData(dragMode) {
    switch (dragMode) {
        case DragMode.PAN:
            return { key: 'DragModeControl.button.pan.tip.txt', icon: Pan };
        case DragMode.RECTANGLE_SELECT:
            return {
                key: 'DragModeControl.button.rectangular.tip.txt',
                icon: RectangularSelection,
            };
        case DragMode.RADIAL_SELECT:
            return { key: 'DragModeControl.button.circular.tip.txt', icon: CircularSelection };
        // VAV: Portable code terms this DragMode as POLYGON_SELECT, however, it is quite
        // free in terms of the shape of selection. So we label it as free-form
        // in the application UI. VAN also does the same thing, so we are consistent.
        case DragMode.POLYGON_SELECT:
        case DragMode.FREEFORM_SELECT:
            return { key: 'DragModeControl.button.freeform.tip.txt', icon: FreeformSelection };
        case DragMode.ZOOM:
            return { key: 'DragModeControl.button.zoom.tip.txt', icon: Zoom };
        case DragMode.INVALID:
        case DragMode.NONE:
        default:
            return;
    }
}
const ElementDecoratorBase = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const [customItems, setCustomItems] = useState([]);
    const getCustomMenuItems = useGetCustomMenuItems();
    const updateCustomItems = useCallback(() => {
        const customItems = getCustomMenuItems({
            type: 'object',
            name: props.id,
        }).map(({ text, callback }, i) => {
            return {
                type: 'overflow',
                text,
                onSelected: callback,
                id: `elementDecorator-customAction-${i}`,
            };
        });
        setCustomItems(customItems);
    }, [getCustomMenuItems, props.id]); // these deps should never change value
    const hasToolbar = !props.selectionDisabled && !props.objectOverlaysDisabled;
    const toolbarVisible = hasToolbar &&
        !((_a = props.busyState) === null || _a === void 0 ? void 0 : _a.isBusy) &&
        (props.focused || props.selected || props.hasMouseHover);
    const warning = props.warningState.type === 'warning' ? props.warningState.message : null;
    // Update custom items when showToolbar becomes true. updateCustomItems should not change.
    useEffect(() => {
        if (toolbarVisible) {
            updateCustomItems();
        }
    }, [updateCustomItems, toolbarVisible]);
    const standardToolbarItems = [];
    {
        const localize = useI18n();
        const elementType = useElementType();
        if (props.maximizeState && elementType !== 'object') {
            const { isMaximized, preferOverflowMenu } = props.maximizeState;
            standardToolbarItems.push({
                type: 'button',
                id: 'elementDecorator-maximize',
                text: isMaximized
                    ? localize({ key: 'ExpandControl.button.restore.txt' })
                    : localize({ key: 'ExpandControl.button.expand.txt' }),
                button: {
                    icon: isMaximized ? NormalView : MaximizeView,
                    tooltip: localize(isMaximized
                        ? localize({ key: 'ExpandControl.button.restore.tip.txt' })
                        : localize({ key: 'ExpandControl.button.expand.tip.txt' })),
                    preferOverflow: preferOverflowMenu,
                },
                onSelected: () => {
                    props.birdActions.toggleMaximizedVisual(props.id);
                },
            });
        }
        if (props.overviewState) {
            const { isOverviewOn } = props.overviewState;
            standardToolbarItems.push({
                type: 'button',
                id: 'elementDecorator-overview',
                text: isOverviewOn
                    ? localize({ key: 'CompositeGraphElementDecorator.hideOverview.txt' })
                    : localize({ key: 'CompositeGraphElementDecorator.showOverview.txt' }),
                button: { icon: ViewNavigationWindow },
                onSelected: () => {
                    props.overviewActions.toggleOverview(props.id); // deprecated
                    props.overviewActions.notifyToggleOverview(props.id);
                },
            });
        }
        if ((_b = props.layerVisibilityState) === null || _b === void 0 ? void 0 : _b.layers.length) {
            standardToolbarItems.push({
                type: 'button',
                id: 'elementDecorator-layerVisiblity',
                text: localize({ key: 'GraphElementDecorator.layers.txt' }),
                button: { icon: Layers },
                subSections: [
                    {
                        items: props.layerVisibilityState.layers.map((layer, i) => ({
                            text: layer.text,
                            icon: layer.icon,
                            checked: layer.visible,
                            keepOpen: true,
                            onSelected: () => props.layerVisibilityActions.notifyToggleLayerVisibility(props.id, i),
                        })),
                    },
                ],
            });
        }
        if (props.dragModeState) {
            const items = [];
            for (const mode of props.dragModeState.availableModes) {
                if (mode === props.dragModeState.currentMode) {
                    continue;
                }
                const data = getDragModeData(mode);
                if (data) {
                    items.push({
                        text: localize({ key: data.key }),
                        onSelected: () => props.dragModeActions.setCurrentMode(props.id, mode),
                        icon: data.icon,
                    });
                }
            }
            const currentItem = getDragModeData(props.dragModeState.currentMode);
            if (currentItem && items.length) {
                standardToolbarItems.push({
                    type: 'button',
                    id: 'elementDecorator-dragMode',
                    text: localize({ key: currentItem.key }),
                    button: { icon: currentItem.icon },
                    subSections: [{ items }],
                });
            }
        }
        if (props.zoomState) {
            standardToolbarItems.push({
                type: 'overflow',
                id: 'elementDecorator-zoom',
                text: localize({ key: 'ZoomControl.button.tip.txt' }),
                subSections: [
                    {
                        items: [
                            {
                                text: localize({ key: 'ZoomControl.button.display.txt' }),
                                // Blank icon needed for text alignment
                                icon: props.zoomState.hideZoomControls ? Blank : Checkmark,
                                onSelected: () => props.zoomActions.toggleZoomControls(props.id),
                                // TODO: should have role="menuitemcheckbox" and set aria-checked
                            },
                        ],
                    },
                    {
                        items: [
                            {
                                text: localize({ key: 'ZoomControl.button.zoomin.txt' }),
                                icon: ZoomInButton,
                                disabled: !props.zoomState.zoomInEnabled,
                                onSelected: () => props.zoomActions.zoomIn(props.id),
                                keepOpen: true,
                            },
                            {
                                text: localize({ key: 'ZoomControl.button.zoomout.txt' }),
                                icon: ZoomOutButton,
                                disabled: !props.zoomState.zoomOutEnabled,
                                onSelected: () => props.zoomActions.zoomOut(props.id),
                                keepOpen: true,
                            },
                        ],
                    },
                    {
                        items: [
                            {
                                text: localize({ key: 'ZoomControl.button.reset.txt' }),
                                icon: ZoomToFit,
                                onSelected: () => props.zoomActions.resetZoom(props.id),
                            },
                        ],
                    },
                ],
            });
        }
        // If the Graph Accelerator is available and this is a graph, add the accelerator to the menu
        if (props.visualizationState.acceleratorEnabled) {
            standardToolbarItems.push({
                type: 'overflow',
                id: 'elementDecorator-accelerator',
                text: localize({ key: 'ObjectToolbarIcon.Accelerator.txt' }),
                onSelected: () => {
                    props.visualizationActions.openGraphAccelerator(props.id);
                },
            });
        }
    }
    return (React.createElement(Fragment, null,
        hasToolbar && props.hasObjectSearch ? (React.createElement(ObjectSearch, { id: props.id, visible: toolbarVisible })) : null,
        hasToolbar ? (React.createElement(ObjectToolbar, { id: props.id, style: { maxWidth: (_c = props.elementDecoratorState) === null || _c === void 0 ? void 0 : _c.maxWidth }, accessibilityLabel: props.accessibilityLabel, autofocusId: props.autofocusMaximizeButton ? 'elementDecorator-maximize' : undefined, visible: toolbarVisible, alwaysShowOverflow: (_d = props.elementDecoratorState) === null || _d === void 0 ? void 0 : _d.alwaysShowOverflow, items: [
                ...standardToolbarItems,
                ...((_f = (_e = props.elementDecoratorState) === null || _e === void 0 ? void 0 : _e.toolbarItems) !== null && _f !== void 0 ? _f : []),
                ...customItems,
            ], buttons: (_g = props.elementDecoratorState) === null || _g === void 0 ? void 0 : _g.toolbarButtons, onOpen: () => {
                props.elementDecoratorActions.notifyToolbarOpen(props.id);
                updateCustomItems();
            } }, !props.zoomState || props.zoomState.hideZoomControls ? null : (React.createElement(ZoomToolbar, { canZoomIn: props.zoomState.zoomInEnabled, canZoomOut: props.zoomState.zoomOutEnabled, onZoomIn: () => props.zoomActions.zoomIn(props.id), onZoomOut: () => props.zoomActions.zoomOut(props.id) })))) : null,
        warning ? (React.createElement(WarningIcon, { warning: warning, iconStyle: {
                position: 'absolute',
                insetInlineEnd: '2px',
                insetBlockEnd: '2px',
            } })) : null));
};
export const ElementDecorator = connect((state, props) => {
    var _a;
    return ({
        focused: state.bird.focusedVisual === props.id,
        selected: state.bird.selectedVisual === props.id,
        accessibilityLabel: state.accessibilityLabel[props.id],
        hasObjectSearch: !!selectObjectSearchState(state, props.id),
        selectionDisabled: (_a = selectSelectionDisabledState(state, props.id)) !== null && _a !== void 0 ? _a : false,
        overviewState: selectOverviewState(state, props.id),
        layerVisibilityState: selectLayerVisibilityState(state, props.id),
        visualizationState: selectVisualizationState(state, props.id),
        zoomState: selectZoomState(state, props.id),
        busyState: selectBusyState(state, props.id),
        dragModeState: selectDragModeState(state, props.id),
        maximizeState: selectMaximizeState(state, props.id),
        elementDecoratorState: selectElementDecoratorState(state, props.id),
        objectOverlaysDisabled: state.ui.objectOverlaysDisabled,
        warningState: selectWarningState(state, props.id),
    });
}, (dispatch) => ({
    overviewActions: bindActionCreators(OverviewState.actions, dispatch),
    layerVisibilityActions: bindActionCreators(LayerVisibilityState.actions, dispatch),
    dragModeActions: bindActionCreators(DragModeState.actions, dispatch),
    zoomActions: bindActionCreators(ZoomState.actions, dispatch),
    visualizationActions: bindActionCreators(VisualizationState.actions, dispatch),
    birdActions: bindActionCreators(BirdState.actions, dispatch),
    elementDecoratorActions: bindActionCreators(ElementDecoratorState.actions, dispatch),
}))(ElementDecoratorBase);
